// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for removing asset ownership assignments.
 * Provides functionality to end customer ownership of an asset,
 * recording the date when the ownership relationship was terminated.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { assetSchema } from '@/asset/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';

/**
 * Schema for validating the API response when unassigning an asset.
 * Transforms the response to provide a more intuitive structure with
 * the asset data separated from metadata.
 */
const unassignAssetResponseSchema = z
  .object({
    data: assetSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, asset: data }));

type UnassignAssetResponse = z.infer<typeof unassignAssetResponseSchema>;

/**
 * Parameters required for unassigning an asset from its current owner.
 * @typedef {Object} UnassignAssetParameters
 * @property {string} date - ISO formatted date when the ownership ended
 */
export interface UnassignAssetParameters {
  date: DateTime;
}

/**
 * Removes ownership assignment from an asset.
 * This function handles the API call to terminate asset ownership and validates
 * the response data using Zod schemas. It records the date when the ownership
 * relationship ended.
 *
 * @param key - Unique identifier of the asset to unassign
 * @param parameters - Object containing the unassignment details
 * @returns Promise resolving to the updated asset data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function unassignAsset(
  key: string,
  parameters: UnassignAssetParameters
): Promise<UnassignAssetResponse> {
  const url = `/v1/asset/${key}/ownership`;

  // The delete method from Axios doesn't allow sending data so the request
  // method is used instead.
  const response = await api.request({
    url,
    method: 'DELETE',
    data: { date: serializeDateForAPI(parameters.date) },
  });

  return parseResponse(unassignAssetResponseSchema, response.data, {
    schemaName: 'UnassignAsset',
    endpoint: url,
  });
}
